// src/components/website-preview/BookingPage.js
import React, { useState, useMemo } from 'react';
import { hexToRgba } from '../../utils/colorUtils';
import { formatPhoneNumber, isValidUSPhone } from '../../utils/phoneFormatter';
import { createBooking } from '../../services/booking-service';
import { downloadICS } from '../../utils/ics-generator';
import LoadingButton from '../common/LoadingButton';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';
// In development, fall back to the local stripe-server (npm start in stripe-server/).
const CHECKOUT_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5001/create-checkout-session'
  : `${FUNCTIONS_BASE}/createCheckoutSession`;
// FormField component - MUST be outside BookingPage to prevent re-mount on every render
const FormField = ({ label, required, type = 'text', value, onChange, error, focused, onFocus, onBlur, placeholder, colors, fonts, maxLength }) => (
  <div>
    <label
      style={{
        display: 'block',
        fontFamily: fonts?.body || 'Montserrat, sans-serif',
        fontSize: '13px',
        fontWeight: '600',
        color: colors?.text || '#1F2937',
        marginBottom: '6px',
      }}
    >
      {label}
      {required && <span style={{ color: colors?.primary || '#7c3aed', marginLeft: '2px' }}>*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      maxLength={maxLength}
      style={{
        width: '100%',
        padding: '10px 14px',
        fontSize: '14px',
        fontFamily: fonts?.body || 'Montserrat, sans-serif',
        color: colors?.text || '#1F2937',
        border: error
          ? '2px solid #EF4444'
          : focused
            ? `2px solid ${colors?.primary || '#7c3aed'}`
            : `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.15)}`,
        borderRadius: '8px',
        outline: 'none',
        transition: 'border 0.15s ease',
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
      }}
    />
    {error && (
      <p
        style={{
          fontSize: '12px',
          color: '#EF4444',
          marginTop: '4px',
          fontFamily: fonts?.body || 'Montserrat, sans-serif',
          margin: '4px 0 0',
        }}
      >
        {error}
      </p>
    )}
  </div>
);

const BookingPage = ({
  service,
  onBack,
  colors,
  fonts,
  buttonStyle,
  availability,
  existingBookings = [],
  practitionerName,
  practitionerId,
  embedded,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [isConfirmingBooking, setIsConfirmingBooking] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_bookingConfirmed, setBookingConfirmed] = useState(null); // { bookingId, calendarSynced }

  // Default availability for when schedule is empty or not configured
  const defaultAvailability = {
    schedule: {
      monday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
      tuesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
      wednesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
      thursday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
      friday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
      saturday: { enabled: false, blocks: [] },
      sunday: { enabled: false, blocks: [] },
    },
    bufferTime: 15,
    bookingWindow: 30,
    minNotice: 24,
    timeOff: [],
  };

  // Use real availability if it has a configured schedule, otherwise use defaults
  const effectiveAvailability = availability?.schedule && Object.keys(availability.schedule).length > 0
    ? availability
    : defaultAvailability;

  // Practitioner's timezone — used for datetime construction and display
  const timezone = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Helper: Get corner radius from buttonStyle
  const getCornerRadius = (style) => {
    const radiusMap = { 'sharp': '0px', 'rounded': '8px', 'very-rounded': '50px' };
    return radiusMap[style?.cornerRadius] || '8px';
  };

  // Helper: Parse time string "09:00" to minutes since midnight
  const parseTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') {
      console.error('Invalid time string:', timeStr);
      return 0;
    }
    const parts = timeStr.split(':');
    const hours = Number(parts[0]) || 0;
    const minutes = Number(parts[1]) || 0;
    const result = hours * 60 + minutes;
    return result;
  };

  // Helper: Format minutes since midnight to "9:00 AM"
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Helper: Format date for suggested slots (e.g., "Wed, Feb 25")
  const formatDateLabel = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  // Helper: Format full date for confirmation (e.g., "Wednesday, February 25, 2026")
  const formatFullDate = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Helper: Get timezone abbreviation (e.g., "PST", "EDT")
  const getTimezoneAbbr = (tz) => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short',
      }).formatToParts(new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
      return '';
    }
  };

  // Helper: Get UTC offset string for ISO 8601 (e.g., "-08:00", "+05:30")
  const getTimezoneOffset = (tz, date) => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'longOffset',
      }).formatToParts(date);
      const tzName = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';
      if (tzName === 'GMT') return '+00:00';
      const match = tzName.match(/GMT([+-])(\d+)(?::(\d+))?/);
      if (!match) return '+00:00';
      const sign = match[1];
      const h = match[2].padStart(2, '0');
      const m = (match[3] || '0').padStart(2, '0');
      return `${sign}${h}:${m}`;
    } catch {
      return '+00:00';
    }
  };

  // Helper: Friendly timezone name (e.g., "Pacific Time", "Eastern Time")
  const getTimezoneDisplay = (tz) => {
    const map = {
      'America/New_York': 'Eastern Time',
      'America/Chicago': 'Central Time',
      'America/Denver': 'Mountain Time',
      'America/Los_Angeles': 'Pacific Time',
      'America/Anchorage': 'Alaska Time',
      'Pacific/Honolulu': 'Hawaii Time',
      'America/Phoenix': 'Arizona Time',
      'Europe/London': 'UK Time',
      'Europe/Paris': 'Central European Time',
      'Asia/Tokyo': 'Japan Time',
      'Australia/Sydney': 'Australian Eastern Time',
    };
    return map[tz] || tz.replace(/_/g, ' ').split('/').pop();
  };

  const tzAbbr = getTimezoneAbbr(timezone);
  const tzDisplay = getTimezoneDisplay(timezone);

  // Check if a date is available for booking
  const isDateAvailable = (date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // 1. Can't book past dates
    if (date < today) return false;

    // 2. Check minimum notice - but allow same-day bookings if time slots are far enough out
    // Don't block the entire date, just filter time slots later
    // For date availability, only block if NO time slots would be available
    // We'll do a simplified check: if it's today, dates are available (slots will be filtered)
    // If it's a future date, it's definitely available

    // 3. Check booking window (days in advance users can book)
    const bookingWindowDays = effectiveAvailability?.bookingWindow || 30;
    const maxDate = new Date(today.getTime() + bookingWindowDays * 24 * 60 * 60 * 1000);
    if (date > maxDate) return false;

    // 4. Check if day of week has working hours
    const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ...
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    const daySchedule = effectiveAvailability?.schedule?.[dayName];

    if (!daySchedule || !daySchedule.enabled || !daySchedule.blocks?.length) {
      return false;
    }

    // 5. Check time off / blocked dates
    const timeOff = effectiveAvailability?.timeOff || [];
    const isBlocked = timeOff.some(block => {
      const blockStart = new Date(block.start);
      const blockEnd = new Date(block.end);
      return date >= blockStart && date <= blockEnd;
    });
    if (isBlocked) return false;

    return true;
  };

  // Generate time slots for a selected date
  const generateTimeSlots = (date) => {
    if (!date) {
      return [];
    }

    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    const daySchedule = effectiveAvailability?.schedule?.[dayName];

    if (!daySchedule?.enabled || !daySchedule?.blocks?.length) {
      return [];
    }

    const slots = [];
    // Ensure serviceDuration is a number (convert from string if needed)
    const serviceDuration = Number(service?.duration) || 60; // minutes
    const buffer = Number(effectiveAvailability?.bufferTime) || 15; // minutes between sessions
    const minNoticeHours = Number(effectiveAvailability?.minNotice) || 24;

    // Get the minimum start time based on minimum notice
    const now = new Date();
    const minStartTime = new Date(now.getTime() + minNoticeHours * 60 * 60 * 1000);

    daySchedule.blocks.forEach((block, blockIndex) => {
      const startMinutes = parseTime(block.start);
      const endMinutes = parseTime(block.end);

      let currentTime = startMinutes;

      while (currentTime + serviceDuration <= endMinutes) {
        // Create a datetime for this slot to check minimum notice
        const slotDateTime = new Date(date);
        slotDateTime.setHours(Math.floor(currentTime / 60));
        slotDateTime.setMinutes(currentTime % 60);
        slotDateTime.setSeconds(0);
        slotDateTime.setMilliseconds(0);

        // Only include slot if it's beyond the minimum notice period
        if (slotDateTime >= minStartTime) {
          slots.push({
            time: currentTime,
            label: formatTime(currentTime),
          });
        }

        currentTime += serviceDuration + buffer;
      }
    });

    return slots;
  };

  // Generate suggested time slots (next 4 available slots across multiple days)
  const getSuggestedSlots = () => {
    const suggestions = [];
    const today = new Date();
    const maxDaysToSearch = 14; // Look up to 2 weeks ahead

    for (let i = 0; i < maxDaysToSearch && suggestions.length < 4; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      // Use the same isDateAvailable check that the calendar uses
      if (!isDateAvailable(date)) continue;

      // Use the same generateTimeSlots function that the calendar uses
      const slots = generateTimeSlots(date);

      for (const slot of slots) {
        if (suggestions.length >= 4) break;
        suggestions.push({
          date: new Date(date),
          time: slot.time,
          timeLabel: slot.label,
          dateLabel: formatDateLabel(date),
        });
      }
    }

    return suggestions;
  };

  // Get days in month for calendar
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0=Sun, 1=Mon, ...

    const days = [];

    // Add empty cells for days before month starts (align with Monday)
    const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Adjust so Monday is first
    for (let i = 0; i < adjustedStart; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const monthDays = getDaysInMonth(currentMonth);
  const timeSlots = generateTimeSlots(selectedDate);

  // Cache suggested slots - only recompute when availability or service duration changes
  const suggestedSlots = useMemo(() => getSuggestedSlots(), [effectiveAvailability, service?.duration]);

  // Form handlers for Step 2
  const handleFieldChange = (field, value) => {
    setClientInfo(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep2 = () => {
    const errors = {};

    if (!clientInfo.firstName.trim()) errors.firstName = 'First name is required';
    if (!clientInfo.lastName.trim()) errors.lastName = 'Last name is required';

    if (!clientInfo.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientInfo.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!clientInfo.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!isValidUSPhone(clientInfo.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirmBooking = async () => {
    setIsConfirmingBooking(true);

    // Build ISO 8601 datetime with explicit timezone offset so the Cloud Function
    // interprets the time in the practitioner's timezone, not UTC.
    //
    // selectedTime is minutes-since-midnight in the practitioner's timezone.
    // We format the date in the practitioner's timezone (en-CA gives YYYY-MM-DD)
    // then append the UTC offset — e.g. "2026-03-05T10:15:00-08:00".
    const hours = Math.floor(selectedTime / 60);
    const mins = selectedTime % 60;
    const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    const tzDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(selectedDate);
    const offset = getTimezoneOffset(timezone, selectedDate);
    const isoDateTime = `${tzDateStr}T${timeStr}${offset}`;

    // ── Paid service: redirect to Stripe Checkout ──────────────────────────────
    if ((service.price || 0) > 0) {
      try {
        // Read practitioner's Stripe Connect account ID from localStorage
        const stripeConnectData = JSON.parse(
          localStorage.getItem('practitioner_stripe_connect') || '{}'
        );
        const stripeConnectAccountId = stripeConnectData.accountId || null;

        const res = await fetch(CHECKOUT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceName: service.name,
            servicePrice: service.price,
            serviceDuration: service.duration,
            practitionerName,
            practitionerId,
            clientEmail: clientInfo.email,
            clientFirstName: clientInfo.firstName,
            clientLastName: clientInfo.lastName,
            clientPhone: clientInfo.phone,
            clientMessage: clientInfo.message,
            dateTime: isoDateTime,
            timezone,
            stripeConnectAccountId,
          }),
        });

        if (!res.ok) throw new Error('Failed to create checkout session');
        const { url } = await res.json();
        // Redirect browser to Stripe Checkout — BookingSuccessPage handles the return
        window.location.href = url;
        return; // Don't clear isConfirmingBooking — page is navigating away
      } catch (err) {
        console.error('Stripe checkout error:', err);
        setIsConfirmingBooking(false);
        return;
      }
    }

    // ── Free service: save booking directly ───────────────────────────────────
    let result = { calendarSynced: false, bookingId: null };

    if (practitionerId) {
      try {
        result = await createBooking({
          practitionerId,
          service: {
            name: service.name,
            duration: service.duration,
            price: service.price,
          },
          client: {
            firstName: clientInfo.firstName,
            lastName: clientInfo.lastName,
            email: clientInfo.email,
            phone: clientInfo.phone,
            message: clientInfo.message,
          },
          dateTime: isoDateTime,
          timezone,
        });
      } catch (err) {
        console.error('Booking sync failed:', err);
        // Still advance to confirmation — don't block the user on a backend error
      }
    }

    setBookingConfirmed(result);
    setIsConfirmingBooking(false);
    setCurrentStep(4);
  };

  const handleDownloadICS = () => {
    const hours = Math.floor(selectedTime / 60);
    const mins = selectedTime % 60;
    const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    const tzDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(selectedDate);
    const offset = getTimezoneOffset(timezone, selectedDate);
    const isoDateTime = `${tzDateStr}T${timeStr}${offset}`;

    downloadICS({
      serviceName: service.name,
      practitionerName,
      dateTimeISO: isoDateTime,
      duration: service.duration,
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const monthYearLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return (
    <div
      style={{
        maxWidth: '960px',
        margin: '0 auto',
        padding: '40px 24px',
        backgroundColor: colors?.background || '#FFFFFF',
        minHeight: '80vh',
      }}
    >
      {/* Back Link */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '14px',
          color: colors?.primary || '#7c3aed',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 0',
          fontWeight: '500',
          fontFamily: fonts?.body || 'Montserrat, sans-serif',
          marginBottom: '24px',
        }}
      >
        ← Back to {service.name}
      </button>

      {/* Main Layout: Left = Booking Form, Right = Service Summary */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: embedded || currentStep === 4 ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '32px',
        }}
      >
        {/* Left Column: Booking Form */}
        <div>
          <h1
            style={{
              fontFamily: fonts?.heading || 'Playfair Display, serif',
              fontSize: '24px',
              fontWeight: '600',
              color: colors?.text || '#1F2937',
              marginBottom: '8px',
            }}
          >
            Book Your Session
          </h1>

          <p
            style={{
              fontSize: '13px',
              color: colors?.secondaryText || '#6B7280',
              fontFamily: fonts?.body || 'Montserrat, sans-serif',
              marginBottom: '32px',
            }}
          >
            Step {currentStep} of 3
          </p>

          {/* Step 1: Date & Time Selection */}
          {currentStep === 1 && (
            <>
              {/* Suggested Times */}
              {suggestedSlots.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  {/* Section title */}
                  <h3
                    style={{
                      fontFamily: fonts?.heading || 'Playfair Display, serif',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: colors?.text || '#1F2937',
                      marginBottom: '12px',
                    }}
                  >
                    Suggested Times
                  </h3>

                  {/* Slot cards — 2 columns */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '10px',
                    }}
                  >
                    {suggestedSlots.map((slot, index) => {
                      const isSelected =
                        selectedDate?.toDateString() === slot.date.toDateString() &&
                        selectedTime === slot.time;

                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setSelectedDate(slot.date);
                            setSelectedTime(slot.time);
                            // Update the calendar's displayed month to match
                            setCurrentMonth(slot.date);
                          }}
                          style={{
                            padding: '12px 14px',
                            borderRadius: '10px',
                            border: isSelected
                              ? `2px solid ${colors?.primary || '#7c3aed'}`
                              : `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.12)}`,
                            backgroundColor: isSelected
                              ? hexToRgba(colors?.primary || '#7c3aed', 0.06)
                              : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = hexToRgba(
                                colors?.primary || '#7c3aed',
                                0.03
                              );
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <span
                            style={{
                              fontFamily: fonts?.body || 'Montserrat, sans-serif',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: isSelected
                                ? colors?.primary || '#7c3aed'
                                : colors?.text || '#1F2937',
                            }}
                          >
                            {slot.dateLabel}
                          </span>
                          <span
                            style={{
                              fontFamily: fonts?.body || 'Montserrat, sans-serif',
                              fontSize: '13px',
                              fontWeight: '400',
                              color: isSelected
                                ? colors?.primary || '#7c3aed'
                                : colors?.secondaryText || '#6B7280',
                            }}
                          >
                            {slot.timeLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Divider with "or" */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginTop: '24px',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: '1px',
                        backgroundColor: hexToRgba(colors?.text || '#1F2937', 0.1),
                      }}
                    />
                    <span
                      style={{
                        fontFamily: fonts?.body || 'Montserrat, sans-serif',
                        fontSize: '12px',
                        color: colors?.secondaryText || '#6B7280',
                        fontWeight: '500',
                      }}
                    >
                      or choose a specific date
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: '1px',
                        backgroundColor: hexToRgba(colors?.text || '#1F2937', 0.1),
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Calendar */}
              <div style={{ marginBottom: '32px' }}>
                <h3
                  style={{
                    fontFamily: fonts?.heading || 'Playfair Display, serif',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: colors?.text || '#1F2937',
                    marginBottom: '16px',
                  }}
                >
                  Select a Date
                </h3>

                <div
                  style={{
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                    backgroundColor: hexToRgba(colors?.primary || '#7c3aed', 0.02),
                  }}
                >
                  {/* Month Navigation */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <button
                      onClick={prevMonth}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: colors?.secondaryText || '#6B7280',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        fontFamily: fonts?.heading || 'Playfair Display, serif',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: colors?.text || '#1F2937',
                      }}
                    >
                      {monthYearLabel}
                    </span>
                    <button
                      onClick={nextMonth}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: colors?.secondaryText || '#6B7280',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}
                    >
                      ›
                    </button>
                  </div>

                  {/* Calendar Grid - Single container for headers + dates */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '4px',
                      textAlign: 'center',
                    }}
                  >
                    {/* Day Headers */}
                    {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                      <div
                        key={day}
                        style={{
                          fontFamily: fonts?.body || 'Montserrat, sans-serif',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: colors?.secondaryText || '#6B7280',
                          padding: '8px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {day}
                      </div>
                    ))}

                    {/* Date Cells */}
                    {monthDays.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} />;
                      }

                      const isAvailable = isDateAvailable(date);
                      const isSelected = selectedDate && date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0];
                      const isToday = date.toISOString().split('T')[0] === todayStr;

                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4px 0',
                          }}
                        >
                          <button
                            onClick={() => {
                              if (isAvailable) {
                                setSelectedDate(date);
                                setSelectedTime(null); // Reset time when date changes
                              }
                            }}
                            disabled={!isAvailable}
                            style={{
                              fontFamily: fonts?.body || 'Montserrat, sans-serif',
                              fontSize: '14px',
                              width: '36px',
                              height: '36px',
                              minWidth: '36px',
                              minHeight: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              padding: '0',
                              cursor: isAvailable ? 'pointer' : 'default',
                              border: isToday && !isSelected ? `1px solid ${colors?.primary || '#7c3aed'}` : 'none',
                              background: isSelected
                                ? colors?.primary || '#7c3aed'
                                : 'none',
                              color: isSelected
                                ? '#FFFFFF'
                                : !isAvailable
                                ? hexToRgba(colors?.text || '#1F2937', 0.25)
                                : colors?.text || '#1F2937',
                              fontWeight: isSelected ? '600' : '400',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              if (isAvailable && !isSelected) {
                                e.currentTarget.style.backgroundColor = hexToRgba(colors?.primary || '#7c3aed', 0.1);
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isAvailable && !isSelected) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {date.getDate()}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <h3
                      style={{
                        fontFamily: fonts?.heading || 'Playfair Display, serif',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: colors?.text || '#1F2937',
                        margin: '0 0 4px',
                      }}
                    >
                      Available Times
                    </h3>
                    {tzAbbr && (
                      <p
                        style={{
                          fontFamily: fonts?.body || 'Montserrat, sans-serif',
                          fontSize: '12px',
                          color: colors?.secondaryText || '#9CA3AF',
                          margin: 0,
                        }}
                      >
                        Times shown in {tzDisplay} ({tzAbbr})
                      </p>
                    )}
                  </div>

                  {timeSlots.length > 0 ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '8px',
                      }}
                    >
                      {timeSlots.map((slot) => {
                        const isSelected = selectedTime === slot.time;
                        return (
                          <button
                            key={slot.time}
                            onClick={() => setSelectedTime(slot.time)}
                            style={{
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: isSelected
                                ? `2px solid ${colors?.primary || '#7c3aed'}`
                                : `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.15)}`,
                              backgroundColor: isSelected
                                ? hexToRgba(colors?.primary || '#7c3aed', 0.08)
                                : 'transparent',
                              fontFamily: fonts?.body || 'Montserrat, sans-serif',
                              fontSize: '13px',
                              fontWeight: isSelected ? '600' : '500',
                              color: isSelected ? colors?.primary || '#7c3aed' : colors?.text || '#1F2937',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = hexToRgba(colors?.primary || '#7c3aed', 0.05);
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {slot.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p
                      style={{
                        fontSize: '13px',
                        color: colors?.secondaryText || '#6B7280',
                        fontFamily: fonts?.body || 'Montserrat, sans-serif',
                        textAlign: 'center',
                        padding: '24px 0',
                      }}
                    >
                      No available times for this date. Please select another day.
                    </p>
                  )}
                </div>
              )}

              {/* Continue Button */}
              <button
                onClick={() => setCurrentStep(2)}
                disabled={!selectedDate || !selectedTime}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  padding: '14px',
                  borderRadius: getCornerRadius(buttonStyle),
                  backgroundColor: (!selectedDate || !selectedTime)
                    ? hexToRgba(colors?.primary || '#7c3aed', 0.4)
                    : colors?.primary || '#7c3aed',
                  color: '#FFFFFF',
                  fontFamily: fonts?.body || 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: (!selectedDate || !selectedTime) ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  margin: '24px auto 0',
                  display: 'block',
                }}
              >
                Continue
              </button>
            </>
          )}

          {/* Step 2: Client Information */}
          {currentStep === 2 && (
            <>
              {/* Selected Time Summary */}
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: '10px',
                  backgroundColor: hexToRgba(colors?.primary || '#7c3aed', 0.05),
                  border: `1px solid ${hexToRgba(colors?.primary || '#7c3aed', 0.12)}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '28px',
                }}
              >
                <div>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: colors?.text || '#1F2937',
                      margin: 0,
                    }}
                  >
                    {formatDateLabel(selectedDate)} · {formatTime(selectedTime)}
                  </p>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: colors?.secondaryText || '#6B7280',
                      margin: '2px 0 0',
                    }}
                  >
                    {service.duration} minutes
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: colors?.primary || '#7c3aed',
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Change
                </button>
              </div>

              {/* Form Fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* First Name + Last Name — side by side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <FormField
                    label="First Name"
                    required
                    value={clientInfo.firstName}
                    onChange={(val) => handleFieldChange('firstName', val)}
                    error={fieldErrors.firstName}
                    focused={focusedField === 'firstName'}
                    onFocus={() => setFocusedField('firstName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Jane"
                    colors={colors}
                    fonts={fonts}
                  />
                  <FormField
                    label="Last Name"
                    required
                    value={clientInfo.lastName}
                    onChange={(val) => handleFieldChange('lastName', val)}
                    error={fieldErrors.lastName}
                    focused={focusedField === 'lastName'}
                    onFocus={() => setFocusedField('lastName')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Smith"
                    colors={colors}
                    fonts={fonts}
                  />
                </div>

                {/* Email */}
                <FormField
                  label="Email"
                  required
                  type="email"
                  value={clientInfo.email}
                  onChange={(val) => handleFieldChange('email', val)}
                  error={fieldErrors.email}
                  focused={focusedField === 'email'}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="jane@example.com"
                  colors={colors}
                  fonts={fonts}
                />

                {/* Phone */}
                <FormField
                  label="Phone"
                  required
                  type="tel"
                  value={clientInfo.phone}
                  onChange={(val) => handleFieldChange('phone', formatPhoneNumber(val))}
                  error={fieldErrors.phone}
                  focused={focusedField === 'phone'}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="(555) 123-4567"
                  colors={colors}
                  fonts={fonts}
                  maxLength={14}
                />

                {/* Message — optional, textarea */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: colors?.text || '#1F2937',
                      marginBottom: '6px',
                    }}
                  >
                    Message to {practitionerName || 'practitioner'}
                    <span
                      style={{
                        fontWeight: '400',
                        color: colors?.secondaryText || '#6B7280',
                        marginLeft: '4px',
                      }}
                    >
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={clientInfo.message}
                    onChange={(e) => handleFieldChange('message', e.target.value)}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Anything you'd like the practitioner to know before your session..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '14px',
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      color: colors?.text || '#1F2937',
                      border:
                        focusedField === 'message'
                          ? `2px solid ${colors?.primary || '#7c3aed'}`
                          : `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.15)}`,
                      borderRadius: '8px',
                      resize: 'vertical',
                      outline: 'none',
                      transition: 'border 0.15s ease',
                      boxSizing: 'border-box',
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>
              </div>

              {/* Navigation Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '28px',
                  maxWidth: '340px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                {/* Back button — outline/secondary style */}
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  style={{
                    flex: '0 0 auto',
                    padding: '12px 20px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: 'transparent',
                    color: colors?.primary || '#7c3aed',
                    border: `1px solid ${colors?.primary || '#7c3aed'}`,
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  ← Back
                </button>

                {/* Continue button — primary style, full remaining width */}
                <button
                  type="button"
                  onClick={() => {
                    if (validateStep2()) {
                      setCurrentStep(3);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: colors?.primary || '#7c3aed',
                    color: '#FFFFFF',
                    border: 'none',
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {/* Step 3: Review & Confirm */}
          {currentStep === 3 && (
            <>
              {/* Appointment Summary Card */}
              <div
                style={{
                  padding: '20px',
                  borderRadius: '10px',
                  border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}
                >
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: colors?.secondaryText || '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      margin: 0,
                    }}
                  >
                    Appointment
                  </p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors?.primary || '#7c3aed',
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                </div>
                <p
                  style={{
                    fontFamily: fonts?.heading || 'Playfair Display, serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors?.text || '#1F2937',
                    margin: '0 0 6px',
                  }}
                >
                  {service.name}
                </p>
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: colors?.text || '#1F2937',
                    margin: '0 0 4px',
                  }}
                >
                  {formatDateLabel(selectedDate)} · {formatTime(selectedTime)}{tzAbbr ? ` ${tzAbbr}` : ''}
                </p>
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '13px',
                    color: colors?.secondaryText || '#6B7280',
                    margin: '0 0 4px',
                  }}
                >
                  {service.duration} minutes · {service.sessionType || 'Virtual'}
                </p>
                {tzAbbr && (
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: colors?.secondaryText || '#6B7280',
                      margin: 0,
                      opacity: 0.75,
                    }}
                  >
                    All times shown in {tzDisplay}
                  </p>
                )}
              </div>

              {/* Your Information Card */}
              <div
                style={{
                  padding: '20px',
                  borderRadius: '10px',
                  border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}
                >
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: colors?.secondaryText || '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      margin: 0,
                    }}
                  >
                    Your Information
                  </p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors?.primary || '#7c3aed',
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                </div>
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: colors?.text || '#1F2937',
                    margin: '0 0 4px',
                  }}
                >
                  {clientInfo.firstName} {clientInfo.lastName}
                </p>
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: colors?.text || '#1F2937',
                    margin: '0 0 4px',
                  }}
                >
                  {clientInfo.email}
                </p>
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    color: colors?.text || '#1F2937',
                    margin: 0,
                  }}
                >
                  {clientInfo.phone}
                </p>
                {clientInfo.message && (
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '13px',
                      color: colors?.secondaryText || '#6B7280',
                      fontStyle: 'italic',
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.08)}`,
                      margin: '12px 0 0',
                      lineHeight: '1.5',
                    }}
                  >
                    "{clientInfo.message}"
                  </p>
                )}
              </div>

              {/* Price Summary Card */}
              <div
                style={{
                  padding: '20px',
                  borderRadius: '10px',
                  border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                  marginBottom: '24px',
                }}
              >
                <p
                  style={{
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: colors?.secondaryText || '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 12px',
                  }}
                >
                  Price
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                    }}
                  >
                    {service.name}
                  </span>
                  <span
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                    }}
                  >
                    ${parseFloat(service.price).toFixed(2)}
                  </span>
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: hexToRgba(colors?.text || '#1F2937', 0.1),
                    margin: '0 0 12px',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: fonts?.heading || 'Playfair Display, serif',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: colors?.text || '#1F2937',
                    }}
                  >
                    Total
                  </span>
                  <span
                    style={{
                      fontFamily: fonts?.heading || 'Playfair Display, serif',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: colors?.text || '#1F2937',
                    }}
                  >
                    ${parseFloat(service.price).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Policy Agreement Text */}
              <p
                style={{
                  fontFamily: fonts?.body || 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: colors?.secondaryText || '#6B7280',
                  textAlign: 'center',
                  lineHeight: '1.5',
                  marginBottom: '20px',
                }}
              >
                By confirming this booking, you agree to the{' '}
                <span
                  style={{
                    color: colors?.primary || '#7c3aed',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  cancellation policy
                </span>
                .
              </p>

              {/* Navigation Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  maxWidth: '340px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  style={{
                    flex: '0 0 auto',
                    padding: '12px 20px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: 'transparent',
                    color: colors?.primary || '#7c3aed',
                    border: `1px solid ${colors?.primary || '#7c3aed'}`,
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  ← Back
                </button>
                <LoadingButton
                  onClick={handleConfirmBooking}
                  loading={isConfirmingBooking}
                  loadingText={(service.price || 0) > 0 ? 'Redirecting to payment…' : 'Confirming…'}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: colors?.primary || '#7c3aed',
                    color: '#FFFFFF',
                    border: 'none',
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: isConfirmingBooking ? 'default' : 'pointer',
                    opacity: isConfirmingBooking ? 0.75 : 1,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {(service.price || 0) > 0
                    ? `Confirm & Pay — $${(service.price).toFixed(2)}`
                    : 'Confirm Booking'}
                </LoadingButton>
              </div>
            </>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 4 && (
            <div
              style={{
                maxWidth: '480px',
                margin: '0 auto',
                textAlign: 'center',
                padding: '40px 20px',
              }}
            >
              {/* Checkmark circle */}
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: hexToRgba(colors?.primary || '#7c3aed', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <span
                  style={{
                    fontSize: '28px',
                    color: colors?.primary || '#7c3aed',
                  }}
                >
                  ✓
                </span>
              </div>

              {/* Heading */}
              <h2
                style={{
                  fontFamily: fonts?.heading || 'Playfair Display, serif',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: colors?.text || '#1F2937',
                  margin: '0 0 8px',
                }}
              >
                Booking Confirmed!
              </h2>

              <p
                style={{
                  fontFamily: fonts?.body || 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: colors?.secondaryText || '#6B7280',
                  margin: '0 0 16px',
                }}
              >
                Your session has been booked with {practitionerName || 'the practitioner'}.
              </p>

              {/* Booking details card */}
              <div
                style={{
                  padding: '24px',
                  borderRadius: '12px',
                  border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                  backgroundColor: hexToRgba(colors?.primary || '#7c3aed', 0.03),
                  textAlign: 'left',
                  marginBottom: '24px',
                }}
              >
                <p
                  style={{
                    fontFamily: fonts?.heading || 'Playfair Display, serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors?.text || '#1F2937',
                    margin: '0 0 12px',
                  }}
                >
                  {service.name}
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                      margin: 0,
                    }}
                  >
                    {formatFullDate(selectedDate)}
                  </p>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                      margin: 0,
                    }}
                  >
                    {formatTime(selectedTime)}{tzAbbr ? ` ${tzAbbr}` : ''} · {service.duration} minutes
                  </p>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '13px',
                      color: colors?.secondaryText || '#6B7280',
                      margin: 0,
                    }}
                  >
                    {service.sessionType || 'Virtual'}
                  </p>
                </div>
              </div>

              {/* Email notice */}
              <p
                style={{
                  fontFamily: fonts?.body || 'Montserrat, sans-serif',
                  fontSize: '13px',
                  color: colors?.secondaryText || '#6B7280',
                  marginBottom: '32px',
                  lineHeight: '1.5',
                }}
              >
                A confirmation will be sent to{' '}
                <span
                  style={{
                    fontWeight: '600',
                    color: colors?.text || '#1F2937',
                  }}
                >
                  {clientInfo.email}
                </span>
              </p>

              {/* Add to calendar + Back to site */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={handleDownloadICS}
                  style={{
                    padding: '10px 24px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: 'transparent',
                    color: colors?.primary || '#7c3aed',
                    border: `1.5px solid ${colors?.primary || '#7c3aed'}`,
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    width: '100%',
                    maxWidth: '300px',
                  }}
                >
                  📅 Add to Your Calendar
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  style={{
                    padding: '10px 32px',
                    borderRadius: getCornerRadius(buttonStyle),
                    backgroundColor: 'transparent',
                    color: colors?.secondaryText || '#6B7280',
                    border: 'none',
                    fontFamily: fonts?.body || 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textDecoration: 'underline',
                    textDecorationColor: 'transparent',
                  }}
                >
                  ← Back to site
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Service Summary */}
        {!embedded && currentStep < 4 && (
          <div>
            <div
              style={{
                padding: '20px',
                borderRadius: '12px',
                border: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                backgroundColor: hexToRgba(colors?.primary || '#7c3aed', 0.03),
                position: 'sticky',
                top: '24px',
              }}
            >
              {/* Service Image */}
              {service.serviceImage && (
                <div
                  style={{
                    width: '100%',
                    height: '140px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '16px',
                  }}
                >
                  <img
                    src={service.serviceImage}
                    alt={service.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              <h3
                style={{
                  fontFamily: fonts?.heading || 'Playfair Display, serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: colors?.text || '#1F2937',
                  marginBottom: '8px',
                }}
              >
                {service.name}
              </h3>

              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  fontSize: '13px',
                  color: colors?.secondaryText || '#6B7280',
                  fontFamily: fonts?.body || 'Montserrat, sans-serif',
                  marginBottom: '8px',
                }}
              >
                <span>{service.duration} min</span>
                <span>·</span>
                <span>{service.sessionType || 'Virtual'}</span>
              </div>

              <p
                style={{
                  fontFamily: fonts?.heading || 'Playfair Display, serif',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: colors?.text || '#1F2937',
                }}
              >
                ${service.price}
              </p>

              {/* Show booking details on steps 2+ */}
              {currentStep >= 2 && selectedDate && selectedTime && (
                <div
                  style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: `1px solid ${hexToRgba(colors?.text || '#1F2937', 0.1)}`,
                  }}
                >
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: colors?.secondaryText || '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '8px',
                      margin: '0 0 8px',
                    }}
                  >
                    Your Booking
                  </p>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                      fontWeight: '500',
                      margin: '4px 0',
                    }}
                  >
                    {formatDateLabel(selectedDate)}
                  </p>
                  <p
                    style={{
                      fontFamily: fonts?.body || 'Montserrat, sans-serif',
                      fontSize: '14px',
                      color: colors?.text || '#1F2937',
                      margin: '4px 0',
                    }}
                  >
                    {formatTime(selectedTime)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
